import API_BASE_URL from '../config';
import ApiUrlManager from './ApiUrlManager';
import { toast } from 'react-toastify';

export class BaseRestDataProvider {
  protected baseUrl: string | undefined;
  // JWT token storage key
  private static readonly TOKEN_KEY = 'jwt_token';

  constructor(endpoint: string) {
    // Guard: Ensure all required values are set before allowing API usage
    if (!BaseRestDataProvider.getToken()) {
      // Do not redirect here; let AuthGate handle login routing
      throw new Error('JWT token is not set. Please authenticate first.');
    }
    try {
      this.baseUrl = `${ApiUrlManager.getApiUrl()}${endpoint}`;
    } catch (e) {
      throw new Error('API base URL or tenant ID is not set. Please authenticate first.');
    }
  }

  // --- JWT Token Management ---
  static setToken(token: string) {
    localStorage.setItem(BaseRestDataProvider.TOKEN_KEY, token);
  }

  static clearToken() {
    localStorage.removeItem(BaseRestDataProvider.TOKEN_KEY);
  }

  static getToken(): string | null {
    return localStorage.getItem(BaseRestDataProvider.TOKEN_KEY);
  }

  // --- Cache-busting: appends a random _cb param to defeat browser/proxy caching ---
  private static cb(url: string): string {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_cb=${Math.random().toString(36).slice(2)}`;
  }

  // --- Helper to add Authorization header if token exists ---
  private static getAuthHeaders(): Record<string, string> {
    const token = BaseRestDataProvider.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  protected async get<T>(url: string): Promise<T> {
    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      headers: {
        ...BaseRestDataProvider.getAuthHeaders(),
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      toast.error('Could not get data');
      throw new Error(errorText);
    }
    return response.json();
  }
  protected async post<T>(url: string, data: any): Promise<T> {

    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...BaseRestDataProvider.getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      toast.error('Could not save new data');
      throw new Error(errorText);
    }

    toast.success('New item saved successfully');

    return response.json();
  }

  protected async postVoid(url: string, data?: any): Promise<void> {
    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...BaseRestDataProvider.getAuthHeaders(),
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      const errorText = await response.text();
      toast.error('Could not perform action');
      throw new Error(errorText);
    }
    toast.success('Action completed successfully');
  }

  protected async put<T>(url: string, data: any): Promise<T | void> {
    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...BaseRestDataProvider.getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      toast.error('Could not update data');
      throw new Error(await response.text());
    }

    toast.success('Changes saved successfully');

    if (response.status === 204) return; // No Content
    const text = await response.text();
    return text ? JSON.parse(text) : undefined;
  }

  protected async delete(url: string): Promise<void> {
    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      method: 'DELETE',
      headers: {
        ...BaseRestDataProvider.getAuthHeaders(),
      },
    });
    if (!response.ok) {
      toast.error('Could not delete data');
      throw new Error(await response.text());
    }
    toast.success('Item deleted successfully');
    // No need to parse response for 204
  }

  protected async getBlob(url: string): Promise<Blob> {
    const response = await fetch(BaseRestDataProvider.cb(`${this.baseUrl}${url}`), {
      headers: {
        ...BaseRestDataProvider.getAuthHeaders(),
      },
    });
    if (!response.ok) {
      toast.error('Could not download file');
      throw new Error(await response.text());
    }
    return response.blob();
  }
}
